import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import EditProgramModal from "../components/grants/programs/EditProgramModal";
import { EmptyState } from "../components/common/EmptyState";
import { programTypeLabels } from "../constants/grants-labels";

interface Program {
  id: string;
  code: string;
  name: string;
  shortName: string | null;
  type: string;
  perspective: string | null;
  website: string | null;
  description: string | null;
  isActive: boolean;
  callsCount: number;
  createdAt: string;
  updatedAt: string;
}

interface ProgramsResponse {
  programs: Program[];
  total: number;
}

/**
 * Page for managing grant programs (FERS, FENG, etc.).
 * Shows programs with their call counts and allows CRUD operations for admins.
 */
const GrantsPrograms: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();
  const canWrite = user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProgramId, setEditingProgramId] = useState<string | null>(null);

  const handleRowClick = (programId: string) => {
    navigate(`/grants/programs/${programId}`);
  };

  const handleRowKeyDown = (e: React.KeyboardEvent, programId: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(`/grants/programs/${programId}`);
    }
  };

  const { data, isLoading, error, refetch } = useQuery<ProgramsResponse>({
    queryKey: ["grants-programs"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/programs");
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/grants/programs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-programs"] });
    },
  });

  const handleDelete = (program: Program) => {
    if (program.callsCount > 0) {
      alert(
        `Nie można usunąć programu "${program.name}" - ma powiązane nabory.`
      );
      return;
    }
    if (
      window.confirm(`Czy na pewno chcesz usunąć program "${program.name}"?`)
    ) {
      deleteMutation.mutate(program.id);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">Programy</h1>
            <p className="mt-1 text-sm text-neutral-600 text-left">
              Lista programów dofinansowania (FERS, FENG, KFS, etc.)
            </p>
          </div>
          {canWrite && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Dodaj program
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie programów...
              </p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-600">Błąd podczas pobierania danych</p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-primary-600 hover:text-primary-800"
              >
                Spróbuj ponownie
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Kod
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Nazwa
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Typ
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Perspektywa
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Naborów
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Status
                    </th>
                    {canWrite && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                        Akcje
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {data?.programs?.map((program) => (
                    <tr
                      key={program.id}
                      className="hover:bg-neutral-50 cursor-pointer focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 focus-visible:outline-none"
                      onClick={() => handleRowClick(program.id)}
                      onKeyDown={(e) => handleRowKeyDown(e, program.id)}
                      tabIndex={0}
                      role="row"
                      aria-label={`Otwórz szczegóły programu ${program.name}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-left">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                          {program.code}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-neutral-900 text-left">
                          {program.name}
                        </div>
                        {program.shortName && (
                          <div className="text-xs text-neutral-500 text-left">
                            {program.shortName}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-left">
                        <span className="text-sm text-neutral-500">
                          {programTypeLabels[program.type] || program.type}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-500">
                        {program.perspective || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {program.callsCount > 0 ? (
                          <Link
                            to={`/grants/calls?programId=${program.id}`}
                            className="text-sm text-primary-600 hover:text-primary-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {program.callsCount}
                          </Link>
                        ) : (
                          <span className="text-sm text-neutral-500">0</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-left">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            program.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-neutral-200 text-neutral-800"
                          }`}
                        >
                          {program.isActive ? "Aktywny" : "Nieaktywny"}
                        </span>
                      </td>
                      {canWrite && (
                        <td className="px-4 py-4 whitespace-nowrap text-right cursor-default">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingProgramId(program.id);
                            }}
                            className="text-primary-600 hover:text-primary-800 mr-3"
                            aria-label={`Edytuj program ${program.name}`}
                          >
                            Edytuj
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(program);
                            }}
                            className="text-red-600 hover:text-red-800"
                            disabled={program.callsCount > 0}
                            aria-label={`Usuń program ${program.name}`}
                          >
                            Usuń
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {(!data?.programs || data.programs.length === 0) && (
                <EmptyState
                  title="Brak programów do wyświetlenia"
                  className="p-6"
                />
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <EditProgramModal
          isOpen={isAddModalOpen || !!editingProgramId}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingProgramId(null);
          }}
          programId={editingProgramId}
        />
      </div>
    </Layout>
  );
};

export default GrantsPrograms;
